/**
 * Progressively enhances the collection's next-page link into an AJAX "load more" action.
 * Numbered pagination links intentionally keep their native navigation behavior.
 */
class CollectionPagination extends HTMLElement {
  #isLoading = false;
  /** @type {AbortController | null} */
  #abortController = null;

  connectedCallback() {
    this.addEventListener('click', this.#handleClick);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#handleClick);
    this.#abortController?.abort();
  }

  /** @param {MouseEvent} event */
  #handleClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const link = event.target.closest('[data-load-more]');
    if (!(link instanceof HTMLAnchorElement) || !this.contains(link)) return;

    const nextUrl = new URL(link.href, window.location.href);
    if (nextUrl.origin !== window.location.origin) return;

    event.preventDefault();
    if (this.#isLoading) return;

    this.#loadMore(link, nextUrl);
  };

  /**
   * @param {HTMLAnchorElement} link
   * @param {URL} nextUrl
   */
  async #loadMore(link, nextUrl) {
    const label = link.querySelector('[data-load-more-label]');
    const originalLabel = label?.textContent ?? '';

    this.#isLoading = true;
    this.#setLoading(link, label, true);
    this.#abortController = new AbortController();

    try {
      const sectionUrl = new URL(nextUrl);
      sectionUrl.searchParams.set('section_id', this.sectionId);
      sectionUrl.searchParams.sort();

      const response = await fetch(sectionUrl.toString(), {
        headers: { Accept: 'text/html' },
        signal: this.#abortController.signal,
      });
      if (!response.ok) throw new Error(`Collection pagination request failed with ${response.status}`);

      const sectionHTML = await response.text();
      const parsedPage = new DOMParser().parseFromString(sectionHTML, 'text/html');
      const incomingController = parsedPage.querySelector('collection-pagination[section-id]');

      if (
        !(incomingController instanceof HTMLElement) ||
        incomingController.getAttribute('section-id') !== this.sectionId
      ) {
        throw new Error('Collection pagination response is missing its controller');
      }

      const currentGrid = this.querySelector('[data-pagination-grid]');
      const currentPagination = this.querySelector('[data-pagination-controls]');
      const incomingGrid = incomingController.querySelector('[data-pagination-grid]');
      const incomingPagination = incomingController.querySelector('[data-pagination-controls]');

      if (
        !(currentGrid instanceof HTMLElement) ||
        !(currentPagination instanceof HTMLElement) ||
        !(incomingGrid instanceof HTMLElement) ||
        !(incomingPagination instanceof HTMLElement)
      ) {
        throw new Error('Collection pagination response is incomplete');
      }

      const expectedPage = Number(nextUrl.searchParams.get('page'));
      const existingIds = new Set(
        Array.from(currentGrid.querySelectorAll(':scope > .amn-collection__grid-item'))
          .map((card) => card.id)
          .filter(Boolean)
      );
      /** @type {HTMLElement[]} */
      const newCards = [];

      for (const card of incomingGrid.querySelectorAll(':scope > .amn-collection__grid-item')) {
        if (!(card instanceof HTMLElement)) continue;
        if (Number.isFinite(expectedPage) && expectedPage > 0 && Number(card.dataset.page) !== expectedPage) {
          throw new Error('Collection pagination response returned an unexpected page');
        }
        if (card.id && existingIds.has(card.id)) continue;

        if (card.id) existingIds.add(card.id);
        newCards.push(card);
      }

      if (newCards.length === 0) {
        throw new Error('Collection pagination response contains no new products');
      }

      const replacementPagination = incomingPagination.cloneNode(true);
      if (!(replacementPagination instanceof HTMLElement)) {
        throw new Error('Collection pagination controls could not be updated');
      }

      const currentlyShown = currentGrid.querySelectorAll(':scope > .amn-collection__grid-item').length;
      const shownAfterLoad = currentlyShown + newCards.length;
      const incomingTotal = Number(incomingController.dataset.total);
      const total = Number.isFinite(incomingTotal) && incomingTotal > 0 ? incomingTotal : shownAfterLoad;
      const replacementStatus = replacementPagination.querySelector('[data-pagination-status]');

      if (replacementStatus) {
        replacementStatus.textContent = this.#formatMessage(this.dataset.countTemplate, {
          shown: shownAfterLoad,
          total,
        });
      }

      const fragment = document.createDocumentFragment();
      fragment.append(...newCards);
      currentGrid.append(fragment);
      currentPagination.replaceWith(replacementPagination);
      this.dataset.total = String(total);

      history.replaceState(
        { ...history.state, collectionPage: expectedPage },
        '',
        nextUrl.toString()
      );

      this.#announce(
        this.#formatMessage(this.dataset.loadedTemplate, {
          count: newCards.length,
          shown: shownAfterLoad,
          total,
        })
      );

      const nextLink = replacementPagination.querySelector('[data-load-more]');
      const status = replacementPagination.querySelector('[data-pagination-status]');
      const focusTarget =
        nextLink instanceof HTMLElement ? nextLink : status instanceof HTMLElement ? status : null;
      focusTarget?.focus({ preventScroll: true });

      this.dispatchEvent(
        new CustomEvent('collection-pagination:loaded', {
          bubbles: true,
          detail: { count: newCards.length, page: expectedPage, shown: shownAfterLoad, total },
        })
      );
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return;
      console.error('Unable to load more collection products', error);
      this.#setLoading(link, label, false, originalLabel);
      this.#announce(this.dataset.errorMessage ?? '');
    } finally {
      this.#isLoading = false;
      this.#abortController = null;
    }
  }

  /**
   * @param {HTMLAnchorElement} link
   * @param {Element | null} label
   * @param {boolean} isLoading
   * @param {string} [restoredLabel]
   */
  #setLoading(link, label, isLoading, restoredLabel = '') {
    link.classList.toggle('amn-collection__load-more--loading', isLoading);
    link.setAttribute('aria-busy', String(isLoading));

    if (isLoading) {
      link.setAttribute('aria-disabled', 'true');
      if (label) label.textContent = this.dataset.loadingLabel ?? label.textContent;
      return;
    }

    link.removeAttribute('aria-disabled');
    link.removeAttribute('aria-busy');
    if (label) label.textContent = restoredLabel;
  }

  /**
   * @param {string | undefined} template
   * @param {Record<string, number>} values
   */
  #formatMessage(template = '', values) {
    return Object.entries(values).reduce(
      (message, [key, value]) => message.split(`[${key}]`).join(String(value)),
      template
    );
  }

  /** @param {string} message */
  #announce(message) {
    const announcer = this.querySelector('[data-pagination-announcer]');
    if (announcer) announcer.textContent = message;
  }

  get sectionId() {
    const id = this.getAttribute('section-id');
    if (!id) throw new Error('The section-id attribute is required');
    return id;
  }
}

if (!customElements.get('collection-pagination')) {
  customElements.define('collection-pagination', CollectionPagination);
}
